import styled from 'styled-components';
import { TextButton } from '../learn_buttons.tsx';
import { resultColorCss } from '../learn_colors.ts';
import { shortScreen } from '../learn_screen_parts.tsx';
import { ReadingHint } from './note_scorer.ts';
import { pitchHintAmber, pitchHintGreen, pitchHoldFillCss } from './pitch_colors.ts';

// The parts of a screen where the singer sings one note against a staff (issues #3518, #3721),
// lifted out of Pitch Match's note screen so Interval Training's sing screen is built from the same
// ones and a change to either reaches both: the row the staff sits in, the hint block under it (the
// hint in large type in the colour its meaning earns, the octave line - always drawn, empty or not,
// so its arrival does not shift what is under it - the hold label and the hold meter, which fills
// yellow while the singer searches and green on a reading that counts), the hint colours, and the
// "Skip this note" link. Each screen picks the hint's words; these only draw.
//
// The test ids keep their Pitch Match names, as the Hearing you meter keeps the Range Finder's: they
// are the values the existing stories match.

export const pitchHintTestId = 'pitch-hint';
export const pitchOctaveNoteTestId = 'pitch-octave-note';
export const pitchHoldLabelTestId = 'pitch-hold-label';
export const pitchHoldMeterTestId = 'pitch-hold-meter';
export const pitchHoldFillTestId = 'pitch-hold-fill';

// "hold 0.6 / 1.0 s" under the hint, from the state machine's hold progress
export function holdLabelText(heldMs: number, holdMs: number): string {
  return `hold ${(heldMs / 1000).toFixed(1)} / ${(holdMs / 1000).toFixed(1)} s`;
}

export const skipNoteText = 'Skip this note';

// The hints both screens show, and their colours: amber while the singer is off the note, green
// once they are on it, the plain grey of a label while nothing is being scored, and the missed red.
// A screen with hints of its own adds their colours to these.
export type NoteHint = 'listen' | 'sing' | 'nudge' | ReadingHint | 'locked' | 'missed';

export const noteHintColors: Record<NoteHint, string> = {
  listen: 'var(--color-text-darker)',
  sing: 'var(--color-text-darker)',
  nudge: 'var(--color-text-darker)',
  higher: pitchHintAmber,
  lower: pitchHintAmber,
  louder: pitchHintAmber, // on the note but too quiet to count: the same amber as being off it
  hold: pitchHintGreen,
  locked: pitchHintGreen,
  missed: resultColorCss.red,
};

// How wide the staff may be drawn on a short screen (iPhone SE), so the hint and the buttons fit
export const shortScreenStaffWidth = 300;

// The row the staff sits in, under the screen's heading
export const NoteStaffRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 10px;

  ${shortScreen} {
    margin-top: 4px;

    svg {
      max-width: ${shortScreenStaffWidth}px;
    }
  }
`;

// "Skip this note", under the hint block; disabled once the note has its outcome
export const SkipNoteLink = ({ id, disabled, onClick }: { id: string; disabled: boolean; onClick: () => void }) => (
  <SkipRow>
    <TextButton id={id} data-testid={id} disabled={disabled} onClick={onClick}>
      {skipNoteText}
    </TextButton>
  </SkipRow>
);

const SkipRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 6px;
`;

export const NoteHintBlock = ({
  hint,
  text,
  color,
  octaveText,
  heldMs,
  holdMs,
  counting,
}: {
  hint: string; // the screen's hint state, for data-hint
  text: string;
  color: string;
  octaveText?: string;
  heldMs: number;
  holdMs: number;
  counting: boolean; // the reading counts toward the hold: the meter fills green
}) => {
  const holdFraction = holdMs > 0 ? heldMs / holdMs : 0;
  return (
    <HintBlock>
      <Hint data-testid={pitchHintTestId} data-hint={hint} $color={color}>
        {text}
      </Hint>
      {/* Always drawn, empty or not, so the line's arrival does not shift the meter under it */}
      <OctaveNote data-testid={pitchOctaveNoteTestId}>{octaveText ?? ''}</OctaveNote>
      <HoldLabel data-testid={pitchHoldLabelTestId}>{holdLabelText(heldMs, holdMs)}</HoldLabel>
      <HoldMeter data-testid={pitchHoldMeterTestId}>
        <HoldFill
          data-testid={pitchHoldFillTestId}
          style={{
            width: `${holdFraction * 100}%`,
            backgroundColor: counting ? pitchHoldFillCss.green : pitchHoldFillCss.yellow,
          }}
        />
      </HoldMeter>
    </HintBlock>
  );
};

const HintBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
`;

const Hint = styled.div<{ $color: string }>`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 24px;
  line-height: 30px;
  text-align: center;
  color: ${({ $color }) => $color};
`;

const OctaveNote = styled.div`
  min-height: 18px;
  font-size: 13px;
  line-height: 18px;
  text-align: center;
  color: var(--color-text-darker);
`;

const HoldLabel = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: var(--color-text-darker);
`;

const HoldMeter = styled.div`
  width: 160px;
  height: 8px;
  border-radius: 4px;
  background-color: var(--color-divider);
  overflow: hidden;
`;

// No transition: the width follows the hold on every frame, and a transition retargeted that often
// never completes in WebKit (issue #3587). The frames, about 90 a second, grow the fill smoothly.
const HoldFill = styled.div`
  height: 100%;
  border-radius: 4px;
`;
